<script lang="ts">
export default {
  name: "BaseListItem",
};
</script>

<script setup lang="ts">
import RelativeDateTime from "@/components/RelativeDateTime.vue";
import ChatAssistantAvatar from "@/components/chat/AssistantAvatar.vue";
import type { AiAssistant, File } from "@/services/fobizzToolsApi";
import { SimpleRouteLocationRaw } from "@/types/router.types";
import { RouteLocationRaw } from "vue-router";

export type BaseListItemType = {
  id: string | number;
  name?: string;
  createdAt?: string;
  image?: File;
};

export type Props = {
  item: BaseListItemType;
  to?: SimpleRouteLocationRaw;
  href?: string;
  icon?: string;
  iconSvg?: string;
  iconColor?: string;
  iconBackgroundColor?: string;
  draggable?: boolean;
  linkDataTestid?: string;
  enableLink?: boolean;
  isAssistant?: boolean;
  isReadonly?: boolean;
  justifyExtraSlotEnd?: boolean;
  tooltip?: string;
  middleJustify?: string;
  extraSlotJustify?: string;
  gap?: string;
};

const props = withDefaults(defineProps<Props>(), {
  to: undefined,
  href: undefined,
  icon: undefined,
  iconSvg: undefined,
  iconColor: "#fff",
  iconBackgroundColor: undefined,
  draggable: false,
  linkDataTestid: undefined,
  enableLink: true,
  isReadonly: false,
  tooltip: undefined,
  middleJustify: "justify-content-between", // Default for middle
  extraSlotJustify: "justify-content-start", // Default for extra slots
  gap: "gap-2", // Default gap
});

const emit = defineEmits(["dragstart", "dragend"]);

const isDragging = ref(false);

const showLink = computed(() => {
  return props.enableLink && (props.to || props.href);
});
const itemAsAssistant = computed(() => {
  return props.item as AiAssistant;
});

const handleDropstart = (_event: DragEvent) => {
  if (!props.draggable) return;
  isDragging.value = true;
  emit("dragstart", props.item);
};

const handleDragend = () => {
  if (!props.draggable) return;
  isDragging.value = false;
  emit("dragend", props.item);
};

const slots = defineSlots<{
  dropdown: unknown;
  icon: unknown;
  label: unknown;
  middle: unknown;
  created: unknown;
  ["extra-1"]: unknown;
  ["extra-2"]: unknown;
}>();
</script>

<template>
  <!-- eslint-disable vuejs-accessibility/no-static-element-interactions -->
  <li
    class="base-list-item"
    :class="{
      'base-list-item--draggable': draggable,
      'base-list-item--dragging': isDragging,
      'base-list-item--readonly': isReadonly,
    }"
    :role="draggable ? 'button' : undefined"
    tabindex="0"
    @dragstart="handleDropstart"
    @dragend="handleDragend"
  >
    <!-- Icon Section -->
    <div
      v-if="slots.icon || icon"
      v-tooltip="{ content: tooltip, placement: 'top' }"
      class="base-list-item__icon"
      :class="{ 'base-list-item__icon--assistant': isAssistant }"
      :style="`background-color: ${iconBackgroundColor ?? iconColor}`"
    >
      <slot name="icon">
        <ChatAssistantAvatar
          v-if="isAssistant && item.image"
          :assistant="itemAsAssistant"
          size="xs"
        />
        <template v-else>
          <i v-if="icon" :class="`${icon}`"></i>
        </template>
      </slot>
    </div>

    <!-- Drag Handler -->
    <div class="base-list-item__handler">
      <i class="bi bi-arrows-move"></i>
    </div>

    <!-- Content Section -->
    <div class="base-list-item__start">
      <div class="base-list-item__label">
        <router-link
          v-if="to && showLink"
          :to="to as RouteLocationRaw"
          class="base-list-item__overlay"
          :data-testid="linkDataTestid || 'link-list-item'"
        >
          <slot name="label">{{ item.name }}</slot>
        </router-link>
        <a
          v-else-if="href && showLink"
          :href="href"
          class="base-list-item__overlay"
          :data-testid="linkDataTestid || 'link-list-item'"
        >
          <slot name="label">{{ item.name }}</slot>
        </a>
        <span v-else class="base-list-item__overlay">
          <slot name="label">{{ item.name }}</slot>
        </span>
      </div>
    </div>

    <div
      class="base-list-item__middle"
      :class="[props.middleJustify, props.gap]"
    >
      <slot name="middle"></slot>
    </div>

    <div
      v-if="slots['extra-1']"
      key="slot-extra-1"
      class="base-list-item__extra"
      :class="[props.extraSlotJustify, props.gap]"
    >
      <div class="slot-extra-1">
        <slot name="extra-1"></slot>
      </div>
    </div>

    <div
      v-if="slots['extra-2']"
      key="slot-extra-2"
      class="base-list-item__extra"
      :class="[props.extraSlotJustify, props.gap]"
    >
      <div class="slot-extra-2">
        <slot name="extra-2"></slot>
      </div>
    </div>

    <!-- End Section -->
    <div class="base-list-item__end">
      <div class="base-list-item__created">
        <slot name="created">
          <RelativeDateTime v-if="item.createdAt" :date="item.createdAt" />
        </slot>
      </div>
    </div>

    <!-- Dropdown Actions -->
    <div v-if="slots.dropdown" class="base-list-item__actions dropdown">
      <button
        :id="`dropdownMenu-${item.id}`"
        :disabled="isReadonly"
        class="base-list-item__button"
        type="button"
        data-bs-toggle="dropdown"
        aria-expanded="false"
        aria-label="Dropdown menu"
      >
        <i class="bi bi-three-dots"></i>
      </button>

      <ul class="dropdown-menu" :aria-labelledby="`dropdownMenu-${item.id}`">
        <slot name="dropdown"></slot>
      </ul>
    </div>
  </li>
  <!-- eslint-enable vuejs-accessibility/no-static-element-interactions -->
</template>

<style lang="scss" scoped>
.base-list-item {
  display: flex;
  align-items: center;
  position: relative;
  padding: $spacer-75;
  text-decoration: none;
  color: $body-color;
  max-height: 3.25rem;

  &::after {
    content: "";
    position: absolute;
    bottom: 0;
    left: $spacer-3;
    right: $spacer-3;
    height: 1px;
    background: $border-color;
  }

  &:last-child {
    &::after {
      display: none;
    }

    border-bottom-left-radius: $border-radius;
    border-bottom-right-radius: $border-radius;
  }

  &:first-child {
    border-top-left-radius: $border-radius;
    border-top-right-radius: $border-radius;
  }

  &:not(.base-list-item--readonly):hover {
    background-color: $hover-color;
  }

  &__overlay {
    &::after {
      content: "";
      position: absolute;
      inset: 0;
      z-index: 0;
    }
  }

  &__handler {
    display: none;
    align-items: center;
    justify-content: center;
    flex: none;
    width: 28px;
    height: 28px;
    font-size: 0.9rem;
  }

  &__icon {
    display: flex;
    align-items: center;
    justify-content: center;
    flex: none;
    margin-right: 0.75rem;
    width: 28px;
    height: 28px;
    border-radius: $border-radius;
    color: $body-color;
    background-color: $gray-200;
    font-size: 0.9rem;

    &--assistant {
      border-radius: 50%;
      overflow: hidden;
    }
  }

  &__start {
    display: flex;
    width: 50%;
    max-width: 325px;
    flex-grow: 1;

    @include media-breakpoint-down(xl) {
      max-width: 290px;
    }

    @include media-breakpoint-down(sm) {
      max-width: 234px;
    }
  }

  &__label {
    font-size: 1rem;
    font-weight: 400;
    margin-bottom: 0;
    overflow: hidden;
    white-space: nowrap;
    text-overflow: ellipsis;

    a {
      color: $body-color;
      text-decoration: none;
      padding: 2rem 0; // a11y fix
    }
  }

  &__middle {
    display: flex;
    gap: $spacer-1;
    width: 20%;
    color: $secondary;
    font-size: $font-size-xs;
    flex-grow: 1;
    align-items: center;
    justify-content: space-between;

    @include media-breakpoint-down(md) {
      display: none;
    }
  }

  &__extra {
    display: flex;
    align-items: center;
    justify-content: flex-start;
    gap: $spacer-1;
    width: 20%;
    color: $secondary;
    font-size: $font-size-xs;
    text-wrap: nowrap;

    & + .base-list-item__extra {
      width: 18%;
      justify-content: flex-start;
      margin-left: 0.75rem;
    }

    @include media-breakpoint-down(lg) {
      display: none;
    }
  }

  &__end {
    display: flex;
    width: 20%;
    margin-left: auto;

    @include media-breakpoint-down(md) {
      width: auto;
    }

    @include media-breakpoint-down(sm) {
      visibility: hidden;
    }
  }

  &__created {
    display: flex;
    overflow: hidden;
    align-items: center;
    margin-left: auto;
    font-size: $font-size-xs;
    color: $secondary;

    time {
      overflow: hidden;
      white-space: nowrap;
      text-overflow: ellipsis;
      text-align: right;
    }

    i {
      font-size: 1.3rem;
    }
  }

  &__actions {
    display: flex;
    width: fit-content;
    margin-left: 0.75rem;

    .dropdown-item {
      display: flex;
      align-items: center;
    }
  }

  &__button {
    background-color: transparent;
    border: 1px solid transparent;
    color: $body-color;
    font-weight: 400;
    padding: 0.25rem 0.5rem;
    border-radius: 0.2rem;
    font-size: 0.8rem;

    &:disabled {
      opacity: 0.5;
    }
  }

  &--dragging {
    opacity: 0.4;
  }

  &--draggable:hover &__icon {
    display: none;
  }

  &--draggable:hover &__handler {
    display: flex;
  }
}

.v-popper--has-tooltip {
  z-index: 9999 !important;
}
</style>
